// @plasser/ui — stateful orchestration hook for the EntityPicker shell.
// Owns query text, active filters, results, loading + hit-count. Search is
// request-id guarded so a slow earlier response can never overwrite a newer one.

import * as React from "react";

import type { EntityRecord, EntityTypeConfig, SearchProvider, SearchResult } from "./types";

export interface UseEntitySearchOptions {
  config: EntityTypeConfig;
  provider: SearchProvider;
  /** Debounce for the text query in ms (default 200). */
  debounceMs?: number;
  /** Page size handed to the provider (default 50). */
  pageSize?: number;
}

export interface UseEntitySearchState {
  text: string;
  setText: (t: string) => void;
  filters: Record<string, string[]>;
  toggleFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  results: EntityRecord[];
  total: number;
  loading: boolean;
  /** True while a follow-up page is being appended (infinite scroll). */
  loadingMore: boolean;
  /** True when more hits exist beyond the loaded page(s). */
  hasMore: boolean;
  error: Error | null;
  /** Re-run the current query (used after a QuickCreate to refresh). */
  refresh: () => void;
  /** Append the next page of hits (drives virtualized infinite scroll). */
  loadMore: () => void;
  /** Optimistically prepend a freshly created record so it shows immediately. */
  prepend: (record: EntityRecord) => void;
}

export function useEntitySearch(opts: UseEntitySearchOptions): UseEntitySearchState {
  const { config, provider, debounceMs = 200, pageSize = 50 } = opts;

  const [text, setText] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [results, setResults] = React.useState<EntityRecord[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [nonce, setNonce] = React.useState(0);

  const requestId = React.useRef(0);
  // Live mirror of results.length so loadMore reads the current offset without
  // being re-created on every page (stable callback for the virtualizer).
  const loadedRef = React.useRef(0);
  loadedRef.current = results.length;
  // Synchronous busy guard for loadMore (a ref, not the state updater — the
  // updater must stay pure; StrictMode double-invokes it).
  const loadingMoreRef = React.useRef(false);
  const hasMore = results.length < total;

  const toggleFilter = React.useCallback((key: string, value: string) => {
    setFilters((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const copy = { ...prev };
      if (next.length === 0) delete copy[key];
      else copy[key] = next;
      return copy;
    });
  }, []);

  const clearFilters = React.useCallback(() => setFilters({}), []);
  const refresh = React.useCallback(() => setNonce((n) => n + 1), []);

  const prepend = React.useCallback((record: EntityRecord) => {
    setResults((prev) => [record, ...prev.filter((r) => r.id !== record.id)]);
    setTotal((t) => t + 1);
  }, []);

  // Append the next page. The busy guard is a ref (synchronous, StrictMode-safe);
  // the side effect lives OUTSIDE any state updater. Race-guarded against the
  // active first-page request so a stale append can never corrupt a fresher query.
  const loadMore = React.useCallback(() => {
    if (loadingMoreRef.current) return;
    const offset = loadedRef.current;
    if (offset >= total) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const id = requestId.current;
    provider
      .search(config, { text, filters, limit: pageSize, offset })
      .then((res: SearchResult) => {
        if (id !== requestId.current) return; // a newer query superseded us
        setResults((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...res.items.filter((r) => !seen.has(r.id))];
        });
        setTotal(res.total);
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [provider, config, text, filters, pageSize, total]);

  // Debounced + race-guarded search.
  React.useEffect(() => {
    const handle = setTimeout(() => {
      const id = ++requestId.current;
      loadingMoreRef.current = false; // a new query supersedes any in-flight loadMore
      setLoading(true);
      setError(null);
      provider
        .search(config, { text, filters, limit: pageSize, offset: 0 })
        .then((res: SearchResult) => {
          if (id !== requestId.current) return; // a newer request superseded us
          setResults(res.items);
          setTotal(res.total);
          setLoading(false);
          setLoadingMore(false);
        })
        .catch((e: unknown) => {
          if (id !== requestId.current) return;
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
          setLoadingMore(false);
        });
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [config, provider, text, filters, pageSize, debounceMs, nonce]);

  return {
    text,
    setText,
    filters,
    toggleFilter,
    clearFilters,
    results,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    refresh,
    loadMore,
    prepend,
  };
}
